import { Dialog, Transition } from '@headlessui/react';
import { BookmarkIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { message, Spin } from 'antd';
import { getAnalytics, logEvent } from 'firebase/analytics';
import React, { Fragment, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store';
import { guestApi } from '../../../../api/guest';
import { menuUrl } from '../../../../env';
import {
  addSavedProduct,
  removeSavedProduct,
} from '../../../../features/auth/auth';
import { AffiliateProduct } from '../../../../interfaces/affiliate_products';
import { SavedItems } from '../../../../interfaces/saved_items';
import { TTUserProfile } from '../../../../interfaces/user';
import { RootState } from '../../../../rootReducer';
import { LoadingIcon } from '../../../components/Spinner';
import { LoginPopUp } from '../../../LogInPopUp';
import { centsToDollars } from '../../../utils';
import TrackingContext from '../../../Providers/TrackingContext';

type Props = {
  product: AffiliateProduct;
  display_partner_id: number;
  onDone: () => void;
};

export function AffiliateProductDetailModal({
  onDone,
  product,
  display_partner_id,
}: Props) {
  const [open, setOpen] = useState(true);
  const isLoggedIn = useSelector<RootState, boolean>(
    state => state.auth.isLoggedIn,
  );
  const { trackFBEvent, trackGAEvent } = useContext(TrackingContext);

  const profile = useSelector<RootState, TTUserProfile | null>(
    state => state.auth.userProfile,
  );
  const bookmarks = useSelector<RootState, SavedItems[]>(
    state => state.auth.saved_products,
  );
  const [savingBookmark, setSavingBookMark] = useState(false);
  const dispatch = useAppDispatch();
  const [showAuth, setShowAuth] = useState(false);

  const handleBookmark = async (userProfile: TTUserProfile) => {
    logEvent(getAnalytics(), 'bookmark_product_clicked', {
      product_id: product.id,
      product_name: product.name,
      display_merchant: display_partner_id,
      is_affiliate: true,
    });
    try {
      setSavingBookMark(true);
      const addProduct = await guestApi.addSavedProduct(userProfile?.id, {
        product_id: product.id,
        product_name: product.name,
        product_type: 'affiliate',
        product_image_url: product.image_url,
        product_base_price: product.base_price,
        guest_id: userProfile?.id,
        merchant_id: display_partner_id,
      });
      dispatch(addSavedProduct(addProduct.data));

      message.success('Product saved for later!');
      setSavingBookMark(false);
      onDone();
    } catch (e) {
      setSavingBookMark(false);
      message.error(e?.response?.data?.detail ?? 'Unable to add the product');
    }
  };
  const removeBookmark = async (id: number) => {
    try {
      setSavingBookMark(true);
      const removedresponse = await guestApi.removeSavedProduct(
        profile?.id,
        id,
      );
      dispatch(removeSavedProduct(id));

      message.success('Bookmark removed');
      setSavingBookMark(false);
      onDone();
    } catch (e) {
      setSavingBookMark(false);
      message.error(
        e?.response?.data?.detail ?? 'Unable to remove the bookmark',
      );
    }
  };
  return (
    <>
      <Transition.Root show={open} as={Fragment} appear>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => {
              setOpen(false);
              onDone();
            }}
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-surface px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8  sm:p-6 lg:py-8 mx-4 lg:mx-16">
                  <div className="p-4 md:p-8 lg:px-16 ">
                    <XCircleIcon
                      className="w-4 h-4 lg:w-6 h-6 text-on-surface top-0 right-0 m-1 fixed"
                      onClick={() => setOpen(false)}
                    />
                    <div className="flex flex-col lg:flex-row lg:space-x-4 lg:space-y-0 space-y-4 items-center lg:items-stretch">
                      {product.image_url && (
                        <img
                          src={product.image_url}
                          className={
                            'w-60 h-60 lg:w-72 lg:h-72 rounded-md object-cover'
                          }
                        />
                      )}
                      <div className="flex flex-col pl-4 py-2 space-y-4 rounded-md bg-surface-variant w-full">
                        <p className=" mb-0  block text-base md:text-lg font-semibold text-on-surface-variant  pointer-events-none">
                          {product?.name}
                        </p>
                        {product?.base_price && (
                          <p className="font-semibold text-base lg:text-lg text-on-surface-variant">
                            Base Price: {centsToDollars(product?.base_price)}
                          </p>
                        )}
                        <p className="block text-on-surface-variant font-medium text-md  pointer-events-none ">
                          {product.description ?? ''}
                        </p>
                        <div className="flex flex-row items-center space-x-4">
                          <a
                            onClick={() => {
                              logEvent(
                                getAnalytics(),
                                'affiliate_product_link_clicked',
                                {
                                  affiliate_product_id: product.id,
                                  affiliate_product_name: product.name,
                                  affiliate_partner: product.affiliate,
                                  display_merchant: display_partner_id,
                                  is_affiliate: true,
                                },
                              );
                              trackGAEvent(
                                'view_item',
                                {
                                  items: [
                                    {
                                      item_id: `aflt_${product.id}`,
                                      item_name: product.name,
                                      affiliation: 'SNAPnTAP',
                                      item_category:
                                        product.tags?.length !== 0
                                          ? product.tags[0]
                                          : 'affiliate',
                                      item_list_id: 'affiliate_product',
                                      item_list_name: 'Affiliate Product',
                                      price: (product.base_price ?? 0) / 100.0,
                                    },
                                  ],
                                },
                                [display_partner_id],
                              );
                              trackFBEvent(
                                'AffiliateLinkClick',
                                {
                                  content_ids: [`aflt_${product.id}`],
                                  content_type: 'affiliate_product',
                                  content_name: [product.name],
                                },
                                true,
                                [display_partner_id],
                              );
                            }}
                            key={product?.unique_id}
                            href={`${menuUrl}/api/v1/affiliate-products/click?unique_id=${
                              product?.unique_id
                            }&merchant_id=${display_partner_id}&utm_campaign=${'affiliate_marketing'}&utm_medium=${'affiliate_product_card'}&utm_source=${'TT_GUEST_APP'}`}
                            target="_blank"
                            className="w-fit"
                          >
                            <div className="bg-tertiary px-2 py-2 w-fit rounded-md text-on-tertiary font-medium text-md hover:text-on-tertiary">
                              Learn more & book
                            </div>
                          </a>
                          <div
                            onClick={async () => {
                              if (isLoggedIn && profile) {
                                if (
                                  bookmarks.filter(
                                    p => p.product_id === product.id,
                                  )?.length === 0
                                ) {
                                  await handleBookmark(profile);
                                } else {
                                  await removeBookmark(
                                    bookmarks.filter(
                                      p => p.product_id === product.id,
                                    )[0]?.id,
                                  );
                                }
                              } else {
                                setShowAuth(true);
                              }
                            }}
                            className="w-fit"
                          >
                            <Spin
                              spinning={savingBookmark}
                              indicator={LoadingIcon}
                            >
                              <div className="bg-tertiary px-2 py-2 w-fit flex flex-row items-center space-x-1 rounded-md text-on-tertiary font-medium text-md hover:text-on-tertiary">
                                <BookmarkIcon className="w-4 h-4" />

                                <div>
                                  {bookmarks.filter(
                                    p => p.product_id === product.id,
                                  )?.length !== 0
                                    ? 'Remove Bookmark'
                                    : 'Add Bookmark'}
                                </div>
                              </div>
                            </Spin>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {showAuth && (
        <LoginPopUp
          title="Enter your phone number to save"
          onSuccess={async (profile: TTUserProfile) => {
            setTimeout(async () => {
              setShowAuth(false);
              await handleBookmark(profile);
            }, 20);
          }}
          onCancel={() => {
            setShowAuth(false);
          }}
        />
      )}
    </>
  );
}
